import React, { useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import Dashboard from "./Dashboard";
import { usePagos } from "../state/PagosContext";

const COLOR_WORTH = "bg-worthblue text-white";
const COLOR_JPGM = "bg-green-700 text-white";

// ======= Helpers fecha (LOCAL) =======
function parseISODate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
function toISODateLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function generarMatrizMes(anio, mes0) {
  const primerDia = new Date(anio, mes0, 1);
  const primerSemana = primerDia.getDay();
  const ultimoDiaMes = new Date(anio, mes0 + 1, 0).getDate();
  const matriz = [];
  let semana = [];
  for (let i = 0; i < primerSemana; i++) semana.push(null);
  for (let d = 1; d <= ultimoDiaMes; d++) {
    if (semana.length === 7) { matriz.push(semana); semana = []; }
    semana.push(d);
  }
  while (semana.length < 7) semana.push(null);
  matriz.push(semana);
  return matriz;
}
function suma(arr, f = (x) => x) { return arr.reduce((a, b) => a + Number(f(b)), 0); }
function money(n) { return `$${Number(n).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`; }
function esWorth(p) { return p.responsable === "Worth"; }
function getColorPorResponsable(resp) { return resp === "Worth" ? COLOR_WORTH : COLOR_JPGM; }

export default function Calendario() {
  const hoy = new Date();
  const [vista, setVista] = useState("mensual"); // mensual | anual | lista
  const [mes, setMes] = useState(hoy.getMonth());
  const [anio, setAnio] = useState(hoy.getFullYear());

  const [responsableFiltro, setResponsableFiltro] = useState("todos");
  const [ordenFiltro, setOrdenFiltro] = useState("todos");
  const [diaFiltro, setDiaFiltro] = useState("");
  const [resaltadoISO, setResaltadoISO] = useState("");

  const [popover, setPopover] = useState(null); // {dia, responsable}
  const [expandedCuenta, setExpandedCuenta] = useState({});

  const {
    pagos,
    catalogo,
    categorias,
    valuadores,
    session,
    cuentasPagoObj,
    cuentasDestinoObj,
    getPendienteMonto,
    aplicarRolloverMes,
    togglePagadoTotal,
    setPagadoPorcentaje,
    setPagadoMonto,
    decidir,
    updatePagoScoped,
    rolloverPago,
    cuentasPago,
    cuentasDestino,
  } = usePagos();

  const role = session?.user?.role || '';
  const canRollover = role === 'admin' || role === 'uso' || role === 'captura';

  // URL params (?vista=&dia=YYYY-MM-DD)
  const location = useLocation();
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const qVista = sp.get("vista");
    const qDia = sp.get("dia");
    if (qVista && ["mensual", "anual", "lista"].includes(qVista)) setVista(qVista);
    if (qDia) {
      const d = parseISODate(qDia);
      if (!isNaN(d)) {
        setAnio(d.getFullYear());
        setMes(d.getMonth());
        setDiaFiltro(qDia);
        setResaltadoISO(qDia);
      }
    }
  }, [location.search]);

  useEffect(() => {
    setResaltadoISO(diaFiltro || "");
  }, [diaFiltro]);

  // Filtros base
  const pagosFiltradosBase = useMemo(() => {
    let arr = pagos;
    if (responsableFiltro !== "todos") arr = arr.filter((p) => p.responsable === responsableFiltro);
    if (ordenFiltro !== "todos") arr = arr.filter((p) => p.ordenServicio === ordenFiltro);
    if (diaFiltro) arr = arr.filter((p) => (p.fechaAjustada || p.fecha) === diaFiltro);
    return arr;
  }, [pagos, responsableFiltro, ordenFiltro, diaFiltro]);

  // Órdenes únicas (Worth)
  const ordenesUnicas = useMemo(
    () => [...new Set(pagos.filter((p) => p.responsable === "Worth" && p.ordenServicio).map((p) => p.ordenServicio))],
    [pagos]
  );

  // En calendario: solo pendientes (saldo > 0)
  const pagosVisibles = useMemo(
    () => pagosFiltradosBase.filter((p) => getPendienteMonto(p) > 0),
    [pagosFiltradosBase, getPendienteMonto]
  );

  // Index día → pagos del mes seleccionado
  const pagosPorDia = useMemo(() => {
    const out = {};
    for (const p of pagosVisibles) {
      const f = parseISODate(p.fechaAjustada || p.fecha);
      if (f.getMonth() === mes && f.getFullYear() === anio) {
        const d = f.getDate();
        if (!out[d]) out[d] = [];
        out[d].push(p);
      }
    }
    return out;
  }, [pagosVisibles, mes, anio]);

  const matrizMes = useMemo(() => generarMatrizMes(anio, mes), [anio, mes]);

  // Totales quincenas/mes
  const { total115, total1631, totalMes } = useMemo(() => {
    let t1 = 0, t2 = 0, tm = 0;
    Object.entries(pagosPorDia).forEach(([d, arr]) => {
      const s = suma(arr, (x) => getPendienteMonto(x));
      if (Number(d) <= 15) t1 += s; else t2 += s; tm += s;
    });
    return { total115: t1, total1631: t2, totalMes: tm };
  }, [pagosPorDia, getPendienteMonto]);

  // Totales por semana
  const totalesSemana = useMemo(() => {
    return matrizMes.map((fila, idx) => {
      const s = fila.reduce((acc, d) => {
        if (!d) return acc;
        const arr = pagosPorDia[d] || [];
        return acc + suma(arr, (x) => getPendienteMonto(x));
      }, 0);
      return { semana: idx + 1, total: s };
    });
  }, [matrizMes, pagosPorDia, getPendienteMonto]);

  // Lista (muestra todos; ordenada LOCAL)
  const pagosLista = useMemo(
    () =>
      pagosFiltradosBase
        .filter((p) => {
          const f = parseISODate(p.fechaAjustada || p.fecha);
          return f.getMonth() === mes && f.getFullYear() === anio;
        })
        .sort((a, b) => parseISODate(a.fechaAjustada || a.fecha) - parseISODate(b.fechaAjustada || b.fecha)),
    [pagosFiltradosBase, mes, anio]
  );

  // ---- acciones / helpers ----
  function cambiaMes(delta) {
    let m = mes + delta, y = anio;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setMes(m); setAnio(y); setPopover(null);
  }
  function limpiarFiltros() {
    setResponsableFiltro("todos");
    setOrdenFiltro("todos");
    setDiaFiltro("");
    setResaltadoISO("");
    setPopover(null);
  }
  function verHoy() {
    const t = new Date();
    const iso = toISODateLocal(t);
    setVista("mensual");
    setAnio(t.getFullYear());
    setMes(t.getMonth());
    setResponsableFiltro("todos");
    setOrdenFiltro("todos");
    setDiaFiltro(iso);
    setResaltadoISO(iso);
    setPopover(null);
  }
  function togglePopover(dia, responsable) {
    setPopover((prev) => (prev && prev.dia === dia && prev.responsable === responsable ? null : { dia, responsable }));
  }
  function toggleCuenta(cuenta) { setExpandedCuenta((prev) => ({ ...prev, [cuenta]: !prev[cuenta] })); }
  function setCuentaBulk(keys, value) {
    setExpandedCuenta((prev) => {
      const next = { ...prev };
      keys.forEach((k) => (next[k] = value));
      return next;
    });
  }

  // Contenido popover (agrupado)
  const contenidoAgrupado = (lista) => {
    const grupos = {};
    for (const p of lista) {
      const c = p.cuenta || "—";
      const sc = p.subcuenta || "—";
      const ssc = p.subsubcuenta || "—";
      const pend = getPendienteMonto(p);
      if (!grupos[c]) grupos[c] = { total: 0, sub: {} };
      grupos[c].total += pend;
      if (!grupos[c].sub[sc]) grupos[c].sub[sc] = { total: 0, sub: {} };
      grupos[c].sub[sc].total += pend;
      if (!grupos[c].sub[sc].sub[ssc]) grupos[c].sub[sc].sub[ssc] = [];
      grupos[c].sub[sc].sub[ssc].push(p);
    }
    const cuentas = Object.keys(grupos);
    const allExp = cuentas.length > 0 && cuentas.every((k) => expandedCuenta[k]);

    return (
      <div className="text-sm">
        <div className="flex justify-end mb-2">
          <button className="text-xs bg-gray-100 hover:bg-gray-200 rounded px-2 py-1" onClick={() => setCuentaBulk(cuentas, !allExp)}>
            {allExp ? "Agrupar todo" : "Desglosar todo"}
          </button>
        </div>
        {cuentas.map((cuenta) => {
          const g = grupos[cuenta];
          const isExp = !!expandedCuenta[cuenta];
          return (
            <div key={cuenta} className="mb-3">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-worthblue">
                  {cuenta} <span className="text-xs text-gray-500">({money(g.total)})</span>
                </div>
                <button className="text-xs bg-gray-100 hover:bg-gray-200 rounded px-2 py-0.5" onClick={() => toggleCuenta(cuenta)}>
                  {isExp ? "Agrupar" : "Desglosar"}
                </button>
              </div>
              {isExp && (
                <div className="mt-1">
                  {Object.entries(g.sub).map(([subc, data]) => (
                    <div key={subc} className="mb-1">
                      <div className="flex justify-between pl-1">
                        <div className="italic">{subc}</div>
                        <div className="font-semibold">{money(data.total)}</div>
                      </div>
                      <ul className="list-disc ml-6 mt-1">
                        {Object.entries(data.sub).map(([subsub, items]) => (
                          <li key={subsub}>
                            <div className="flex justify-between">
                              <span>{subsub !== "—" ? subsub : "—"}</span>
                              <span className="font-semibold">{money(suma(items, (x) => getPendienteMonto(x)))}</span>
                            </div>
                            <ul className="ml-5">
                              {items.map((p) => (
                                <li key={p.id} className="truncate">
                                  {p.nombre}
                                  {esWorth(p) && p.ordenServicio ? <span className="italic ml-1">[OS: {p.ordenServicio}]</span> : null}
                                  {p.clasificacion ? <span className="ml-1 text-xs bg-gray-100 px-1 py-0.5 rounded">{p.clasificacion}</span> : null}
                                  {" — "}<b>{money(getPendienteMonto(p))}</b>
                                  {canRollover && (
                                    <button
                                      className="ml-2 text-xs bg-gray-100 hover:bg-gray-200 rounded px-2 py-0.5"
                                      title="Mover al mes siguiente"
                                      onClick={async (e) => { e.preventDefault(); e.stopPropagation(); const curDay = Number((p.fecha||"").split("-")[2]||1); const s = prompt("Día del mes al que se moverá en el siguiente mes (1-31):", String(curDay)); if (!s) return; const d = Math.max(1, Math.min(31, Number(s))); await rolloverPago(p.id, d); setPopover(null); }}
                                    >
                                      Rollover
                                    </button>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ============== RENDER =================
  return (
    <div className="max-w-5xl mx-auto py-6 px-3">
      <Dashboard mes={mes} anio={anio} />

      {/* Header / Filtros */}
      <div className="flex flex-wrap items-center justify-between mb-4 gap-2">
        <h2 className="font-worth text-2xl font-bold text-worthblue">Calendario de Pagos</h2>

        <div className="flex flex-wrap gap-2 items-center">
          <select className="border px-2 py-1 rounded" value={vista} onChange={(e) => setVista(e.target.value)}>
            <option value="anual">Anual</option>
            <option value="mensual">Mensual</option>
            <option value="lista">Lista</option>
          </select>

          <select className="border px-2 py-1 rounded" value={responsableFiltro} onChange={(e) => setResponsableFiltro(e.target.value)}>
            <option value="todos">Todos</option>
            <option value="Worth">Worth</option>
            <option value="JPGM">JPGM</option>
          </select>

          <select
            className="border px-2 py-1 rounded"
            value={ordenFiltro}
            onChange={(e) => setOrdenFiltro(e.target.value)}
            disabled={responsableFiltro !== "Worth"}
          >
            <option value="todos">Todas las órdenes</option>
            {[...new Set(Object.values(pagos).filter(p => p.responsable === "Worth").map(p => p.ordenServicio).filter(Boolean))].map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>

          <select className="border px-2 py-1 rounded" value={diaFiltro} onChange={(e) => setDiaFiltro(e.target.value)}>
            <option value="">Todos los días</option>
            {Object.keys(pagosPorDia).map((d) => {
              const f = new Date(anio, mes, Number(d));
              return <option key={d} value={toISODateLocal(f)}>Día {d}</option>;
            })}
          </select>

          {/* Navegación */}
          {vista !== "anual" && (
            <>
              <button className="px-2" onClick={() => cambiaMes(-1)}>{"<"}</button>
              <span>{new Date(anio, mes).toLocaleString("es-MX", { month: "long", year: "numeric" })}</span>
              <button className="px-2" onClick={() => cambiaMes(1)}>{">"}</button>
            </>
          )}
          {vista === "anual" && (
            <>
              <button className="px-2" onClick={() => setAnio(anio - 1)}>{"<"}</button>
              <span>{anio}</span>
              <button className="px-2" onClick={() => setAnio(anio + 1)}>{">"}</button>
            </>
          )}

          {/* Acciones rápidas */}
          <button className="ml-2 px-3 py-1 rounded bg-gray-100 hover:bg-gray-200" onClick={limpiarFiltros}>Limpiar</button>
          <button className="px-3 py-1 rounded bg-worthblue text-white font-semibold hover:bg-worthgold" onClick={verHoy}>Ver hoy</button>

          {/* Rollover */}
          {vista === "mensual" && (
            <button
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200"
              title="Mover pagos pendientes de este mes al mismo día del siguiente mes"
              onClick={() => aplicarRolloverMes(anio, mes)}
            >
              Rollover mes
            </button>
          )}

          {/* Acceso a Configuración */}
          <Link
            to="/configuracion"
            className="px-3 py-1 rounded bg-white border text-worthblue hover:bg-gray-50 font-semibold"
            title="Administrar categorías y cuentas"
          >
            Configuración
          </Link>
        </div>
      </div>

      {/* ======= MENSUAL ======= */}
      {vista === "mensual" && (
        <div className="bg-white rounded-xl shadow p-4">
          {/* Encabezado días */}
          <div className="grid grid-cols-7 border-b font-bold text-worthblue text-lg">
            {["DOM.","LUN.","MAR.","MIÉ.","JUE.","VIE.","SÁB."].map((d) => (
              <div key={d} className="py-2 border-r last:border-r-0 text-center">{d}</div>
            ))}
          </div>

          {/* Semanas */}
          <div>
            {matrizMes.map((semana, i) => (
              <div key={i} className="grid grid-cols-7 border-b last:border-b-0" style={{ minHeight: 80 }}>
                {semana.map((dia, j) => {
                  const items = dia ? (pagosPorDia[dia] || []) : [];
                  const totalDia = suma(items, (x) => getPendienteMonto(x));
                  const worthHoy = items.filter((x) => x.responsable === "Worth");
                  const jpgmHoy  = items.filter((x) => x.responsable === "JPGM");
                  const showWorth = worthHoy.length > 0;
                  const showJpgm = jpgmHoy.length > 0;
                  const isWeekendCol = j === 0 || j === 6;
                  const popoverAbierto = popover && popover.dia === dia;

                  let isResaltado = false;
                  if (dia && resaltadoISO) {
                    const r = parseISODate(resaltadoISO);
                    if (!isNaN(r)) { isResaltado = (r.getFullYear() === anio && r.getMonth() === mes && r.getDate() === dia); }
                  }

                  return (
                    <div
                      key={j}
                      className={`relative border-r last:border-r-0 px-2 py-1 align-top ${isWeekendCol ? "bg-gray-50" : ""} ${!dia ? "bg-gray-200" : ""} ${isResaltado ? "ring-2 ring-worthgold ring-offset-1 ring-offset-white rounded-md" : ""}`}
                      style={{ minHeight: 80 }}
                    >
                      {dia && (
                        <>
                          {/* Día + total */}
                          <div className="flex justify-between items-start font-semibold">
                            <span>{dia}</span>
                            <span className="text-xs text-worthblue">{totalDia > 0 ? money(totalDia) : ""}</span>
                          </div>

                          {/* Círculos por responsable */}
                          <div className="mt-1 flex gap-1">
                            {showWorth && (
                              <button
                                className="w-3.5 h-3.5 rounded-full bg-worthblue"
                                title="Pagos Worth"
                                onClick={(e) => { e.stopPropagation(); togglePopover(dia, "Worth"); }}
                              />
                            )}
                            {showJpgm && (
                              <button
                                className="w-3.5 h-3.5 rounded-full bg-green-700"
                                title="Pagos JPGM"
                                onClick={(e) => { e.stopPropagation(); togglePopover(dia, "JPGM"); }}
                              />
                            )}
                          </div>

                          {/* Popover detalle */}
                          {popoverAbierto && popover?.responsable && (
                            <div
                              className="absolute z-20 top-7 right-2 w-96 bg-white border border-gray-200 rounded-lg shadow-xl p-3"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className={`px-2 py-0.5 rounded-full text-xs font-bold ${getColorPorResponsable(popover.responsable)}`}>
                                  {popover.responsable}
                                </div>
                                <button className="text-gray-500 hover:text-gray-700 text-sm" onClick={() => setPopover(null)}>✕</button>
                              </div>
                              {popover.responsable === "Worth"
                                ? contenidoAgrupado(worthHoy)
                                : contenidoAgrupado(jpgmHoy)}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Totales por semana */}
          <div className="mt-4">
            <div className="text-sm text-gray-600 mb-1">Totales por semana</div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
              {totalesSemana.map((w) => (
                <div key={w.semana} className="bg-gray-50 rounded-lg p-2 text-center">
                  <div className="text-xs text-gray-500">Semana {w.semana}</div>
                  <div className="font-bold text-worthblue">{money(w.total)}</div>
                </div>
              ))}
              <div className="bg-gray-100 rounded-lg p-2 text-center">
                <div className="text-xs text-gray-500">Total mes</div>
                <div className="font-bold text-worthgold">{money(totalMes)}</div>
              </div>
            </div>
          </div>

          {/* Notas y totales quincena/mes */}
          <div className="border-t mt-3 p-3 text-worthblue">
            <b>NOTAS</b>
            <div className="text-gray-500 text-sm">Agrega aquí notas importantes del mes...</div>
          </div>
          <div className="flex flex-col md:flex-row gap-3 mt-4 justify-around font-bold text-lg">
            <div>1–15: <span className="text-worthblue">{money(total115)}</span></div>
            <div>16–31: <span className="text-worthblue">{money(total1631)}</span></div>
            <div>Total mes: <span className="text-worthgold">{money(totalMes)}</span></div>
          </div>
        </div>
      )}

      {/* ======= ANUAL ======= */}
      {vista === "anual" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Array.from({ length: 12 }, (_, i) => {
            const listaMes = pagosVisibles.filter((p) => {
              const f = parseISODate(p.fechaAjustada || p.fecha);
              return f.getMonth() === i && f.getFullYear() === anio;
            });
            const total = suma(listaMes, (x) => getPendienteMonto(x));
            const cuentas = {};
            for (const p of listaMes) {
              const key = p.cuenta || "—";
              cuentas[key] = (cuentas[key] || 0) + getPendienteMonto(p);
            }
            return (
              <div key={i} className="bg-white rounded-xl shadow p-3">
                <div className="font-bold text-worthblue text-lg mb-2 flex justify-between">
                  <span>{new Date(anio, i).toLocaleString("es-MX", { month: "long" })}</span>
                  <span className="text-worthgold">{total ? money(total) : ""}</span>
                </div>
                {Object.entries(cuentas).map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <div className="font-semibold text-worthblue">{k}</div>
                    <div>{money(v)}</div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* ======= LISTA (edición + parciales) ======= */}
            {vista === "lista" && (
        <ListaEditable
          pagos={pagosLista}
          getPendienteMonto={getPendienteMonto}
          togglePagadoTotal={togglePagadoTotal}
          setPagadoPorcentaje={setPagadoPorcentaje}
          setPagadoMonto={setPagadoMonto}
          decidir={decidir}
          rolloverPago={rolloverPago}
          updatePagoScoped={updatePagoScoped}
          catalogo={catalogo}
          categorias={categorias}
          valuadores={valuadores}
          cuentasPagoObj={cuentasPagoObj}
          cuentasDestinoObj={cuentasDestinoObj}
          session={session}
        />
      )}
    </div>
  );
}

// ====== Subcomponente: Lista Editable ======
function ListaEditable({
  pagos,
  getPendienteMonto,
  togglePagadoTotal,
  setPagadoPorcentaje,
  setPagadoMonto,
  decidir,
  rolloverPago,
  updatePagoScoped,
  catalogo,
  categorias,
  valuadores,
  cuentasPagoObj,
  cuentasDestinoObj,
  session,
}) {
  const role = session?.user?.role || "";
  const canEdit = role === "admin" || role === "captura";
  const canUso = role === "admin" || role === "uso";
  const canRollover = role === "admin" || role === "uso" || role === "captura";

  const [editKey, setEditKey] = useState(null); // `${scope}:${id}`
  const [draft, setDraft] = useState({});
  const [sort, setSort] = useState({ key: "date", dir: "asc" });

  const pagosOrdenados = useMemo(() => {
    const arr = [...pagos];
    const dir = sort.dir === "desc" ? -1 : 1;

    const getVal = (p) => {
      switch (sort.key) {
        case "date": return p.fecha;
        case "name": return p.nombre || "";
        case "responsable": return p.responsable || "";
        case "catalog": return p.catalog_path || "";
        case "classification": return p.clasificacion || "";
        case "orden_servicio": return p.ordenServicio || "";
        case "payment": return p.cuentaPago || "";
        case "destination": return p.cuentaDestino || "";
        case "amount": return Number(p.monto || 0);
        case "paid": return Number(p.paid_amount || 0);
        case "pending": return Number(getPendienteMonto(p) || 0);
        case "status": return p.status || "";
        default: return p.fecha;
      }
    };

    arr.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb), "es", { numeric: true, sensitivity: "base" }) * dir;
    });

    return arr;
  }, [pagos, sort, getPendienteMonto]);

  const totalLista = useMemo(
    () => pagosOrdenados.reduce((acc, p) => acc + getPendienteMonto(p), 0),
    [pagosOrdenados, getPendienteMonto]
  );

  const money = (n) => `$${Number(n).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;

  const scopeOf = (p) => (p.responsable === "JPGM" ? "jpgm" : "worth");

  const catalogOptionsFor = (p) => {
    const scope = scopeOf(p);
    return catalogo
      .filter((c) => c.type === "expense" && (c.scope === scope || c.scope === "both"))
      .map((c) => ({ id: c.id, label: [c.level1, c.level2, c.level3, c.name].filter(Boolean).join(" / ") }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  const catsFor = (p) => categorias[p.responsable] || [];

  const cuentasPagoFor = (p) => (cuentasPagoObj[p.responsable] || []);
  const cuentasDestinoFor = (p) => (cuentasDestinoObj[p.responsable] || []);

  const startEdit = (p) => {
    if (!canEdit) return;
    setEditKey(`${p.scope}:${p.id}`);
    setDraft({
      name: p.nombre,
      date: p.fecha,
      amount: Number(p.monto),
      catalog_account_id: p.catalog_account_id || "",
      classification: p.clasificacion || "",
      orden_servicio: p.ordenServicio || "",
      payment_account_id: p.payment_account_id || "",
      destination_account_id: p.destination_account_id || "",
      valuador_id: p.valuador_id || "",
    });
  };

  const cancelEdit = () => {
    setEditKey(null);
    setDraft({});
  };

  const saveEdit = async (p) => {
    if (!canEdit) return;
    const scope = p.scope || scopeOf(p);
    const patch = {
      name: (draft.name || "").trim(),
      date: draft.date,
      amount: Number(draft.amount),
      catalog_account_id: Number(draft.catalog_account_id),
      classification: draft.classification,
      orden_servicio: draft.orden_servicio || "",
      payment_account_id: Number(draft.payment_account_id),
      destination_account_id: Number(draft.destination_account_id),
      valuador_id: draft.valuador_id ? Number(draft.valuador_id) : null,
    };
    await updatePagoScoped(scope, p.id, patch);
    cancelEdit();
  };

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  };

  const Th = ({ k, children }) => (
    <th className="py-2 cursor-pointer select-none" onClick={() => toggleSort(k)} title="Ordenar">
      {children} {sort.key === k ? (sort.dir === "asc" ? "▲" : "▼") : ""}
    </th>
  );

  const askRolloverDay = async (p) => {
    if (!canRollover) return;
    const curDay = Number((p.fecha || "").split("-")[2] || 1);
    const s = prompt("Día del mes al que se moverá en el siguiente mes (1-31):", String(curDay));
    if (!s) return;
    const d = Math.max(1, Math.min(31, Number(s)));
    await rolloverPago(p.id, d);
  };

  const askPaidPct = async (p) => {
    if (!canUso) return;
    const curPct = p.monto ? Math.round((Number(p.paid_amount || 0) * 100) / Number(p.monto)) : 0;
    const s = prompt("Porcentaje pagado (0-100):", String(curPct));
    if (s === null) return;
    const pct = Math.max(0, Math.min(100, Number(s)));
    await setPagadoPorcentaje(p.id, pct);
  };

  const askPaidMonto = async (p) => {
    if (!canUso) return;
    const s = prompt("Monto pagado (MXN):", String(Number(p.paid_amount || 0)));
    if (s === null) return;
    const m = Math.max(0, Number(String(s).replace(/[^\d.]/g, "")) || 0);
    await setPagadoMonto(p.id, m);
  };

  return (
    <div className="bg-white rounded-xl shadow p-4 overflow-x-auto">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm text-gray-600">
          Total pendiente en lista: <b className="text-worthblue">{money(totalLista)}</b>
        </div>
        <div className="text-xs text-gray-500">Orden actual: {sort.key} {sort.dir}</div>
      </div>

      <table className="w-full text-left text-sm whitespace-nowrap">
        <thead>
          <tr className="border-b text-worthblue font-bold">
            <Th k="date">Fecha</Th>
            <Th k="name">Pago</Th>
            <Th k="responsable">Resp.</Th>
            <Th k="catalog">Cuenta (catálogo)</Th>
            <Th k="classification">Clasificación</Th>
            <Th k="orden_servicio">OS</Th>
            <Th k="payment">Cuenta pago</Th>
            <Th k="destination">Cuenta destino</Th>
            <Th k="amount">Monto</Th>
            <Th k="paid">Pagado</Th>
            <Th k="pending">Pendiente</Th>
            <Th k="status">Estatus</Th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {pagosOrdenados.length === 0 && (
            <tr><td colSpan={13} className="text-center text-gray-400 py-5">Sin pagos en este periodo</td></tr>
          )}

          {pagosOrdenados.map((p) => {
            const key = `${p.scope}:${p.id}`;
            const isEdit = editKey === key;
            const pendiente = getPendienteMonto(p);
            const scope = p.scope || scopeOf(p);

            const catalogOptions = catalogOptionsFor(p);
            const cats = catsFor(p);
            const outAcc = cuentasPagoFor(p);
            const inAcc = cuentasDestinoFor(p);

            return (
              <tr key={key} className="border-b">
                <td className="py-1">
                  {isEdit
                    ? <input type="date" className="border rounded px-2 py-1" value={draft.date || ""} onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))} />
                    : (p.fecha)}
                </td>

                <td className="max-w-[18rem] truncate">
                  {isEdit
                    ? <input className="border rounded px-2 py-1 w-72" value={draft.name || ""} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
                    : p.nombre}
                </td>

                <td>
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${p.responsable === "Worth" ? "bg-worthblue text-white" : "bg-green-700 text-white"}`}>
                    {p.responsable}
                  </span>
                </td>

                <td className="max-w-[20rem] truncate">
                  {isEdit ? (
                    <select className="border rounded px-2 py-1 w-80" value={draft.catalog_account_id} onChange={(e) => setDraft((d) => ({ ...d, catalog_account_id: e.target.value }))}>
                      <option value="">—</option>
                      {catalogOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  ) : (
                    p.catalog_path || "—"
                  )}
                </td>

                <td>
                  {isEdit ? (
                    <select className="border rounded px-2 py-1" value={draft.classification || ""} onChange={(e) => setDraft((d) => ({ ...d, classification: e.target.value }))}>
                      <option value="">—</option>
                      {cats.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  ) : (
                    p.clasificacion || "—"
                  )}
                </td>

                <td>
                  {isEdit
                    ? <input className="border rounded px-2 py-1 w-28" value={draft.orden_servicio || ""} onChange={(e) => setDraft((d) => ({ ...d, orden_servicio: e.target.value }))} />
                    : (p.ordenServicio || "—")}
                </td>

                <td>
                  {isEdit ? (
                    <select className="border rounded px-2 py-1" value={draft.payment_account_id} onChange={(e) => setDraft((d) => ({ ...d, payment_account_id: e.target.value }))}>
                      <option value="">—</option>
                      {outAcc.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  ) : (
                    p.cuentaPago || "—"
                  )}
                </td>

                <td>
                  {isEdit ? (
                    <select className="border rounded px-2 py-1" value={draft.destination_account_id} onChange={(e) => setDraft((d) => ({ ...d, destination_account_id: e.target.value }))}>
                      <option value="">—</option>
                      {inAcc.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  ) : (
                    p.cuentaDestino || "—"
                  )}
                </td>

                <td className="font-semibold">{money(p.monto)}</td>
                <td>{money(p.paid_amount || 0)}</td>
                <td className="font-semibold">{money(pendiente)}</td>

                <td>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    p.status === "approved" ? "bg-green-50 text-green-800" :
                    p.status === "rejected" ? "bg-red-50 text-red-800" :
                    "bg-gray-100 text-gray-700"
                  }`}>
                    {p.status || "pending"}
                  </span>
                </td>

                <td>
                  <div className="flex flex-wrap gap-1">
                    {canUso && (
                      <>
                        <button className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" title="Aprobar" onClick={() => decidir(p.id, "approved")}>Aprobar</button>
                        <button className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" title="Rechazar" onClick={() => decidir(p.id, "rejected")}>Rechazar</button>
                        <button className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" title="Definir pagado por porcentaje" onClick={() => askPaidPct(p)}>%</button>
                        <button className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" title="Definir pagado por monto" onClick={() => askPaidMonto(p)}>$</button>
                        <button className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" title="Toggle pagado total" onClick={() => togglePagadoTotal(p.id)}>✔</button>
                      </>
                    )}

                    {canRollover && (
                      <button className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" title="Mover al mes siguiente con día elegido" onClick={() => askRolloverDay(p)}>
                        Rollover
                      </button>
                    )}

                    {canEdit && !isEdit && (
                      <button className="px-2 py-1 rounded bg-worthblue text-white hover:bg-worthgold" onClick={() => startEdit(p)}>Editar</button>
                    )}
                    {canEdit && isEdit && (
                      <>
                        <button className="px-2 py-1 rounded bg-worthblue text-white hover:bg-worthgold" onClick={() => saveEdit(p)}>Guardar</button>
                        <button className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" onClick={cancelEdit}>Cancelar</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {!canEdit && (
        <div className="text-xs text-gray-500 mt-3">
          Nota: tu perfil no permite editar campos. Si eres <b>uso</b>, puedes aprobar/rechazar y registrar pagos parciales.
        </div>
      )}
    </div>
  );
}
